import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import {
  provideHttpClient,
  withFetch,
  HttpBackend,
  withInterceptors,
  withInterceptorsFromDi,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
} from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import {
  InitializeService,
  initializeApp,
} from './share/services/initialize.service';
import { ELanguageCode } from './share/utils/enums/country.enum';
import { routes } from './shrl.routes';
import { apiInterceptor } from './core/interceptors/api.interceptor';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { apiErrorInterceptor } from './core/interceptors/api-error.interceptor';
import { loaderInterceptor } from './core/interceptors/loader.interceptor';
import { LanguageRedirectInterceptor } from './core/interceptors/language-redirect.interceptor';
import { registerLocaleData } from '@angular/common';
import localePl from '@angular/common/locales/pl';
import { environment } from '../environments/environment';

registerLocaleData(localePl, 'pl-PL');

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),

    // interceptors order matters!
    provideHttpClient(
      withFetch(),
      withInterceptors([
        apiInterceptor,
        apiErrorInterceptor,
        loaderInterceptor,
      ]),
      withInterceptorsFromDi()
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },

    provideAnimations(),
    provideEnvironmentNgxMask(),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory2,
          deps: [HttpBackend],
        },
        defaultLanguage: ELanguageCode.PL,
      })
    ),

    // MATERIAL
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline', subscriptSizing: 'dynamic' },
    },
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
    provideNativeDateAdapter(),
    { provide: LOCALE_ID, useValue: 'pl-PL' },

    // CUSTOM INITIALIZER
    InitializeService,
    provideAppInitializer(() => {
      const initializerFn = initializeApp(inject(InitializeService));
      return initializerFn();
    }),

    // LANGUAGE REDIRECT INTERCEPTOR
    LanguageRedirectInterceptor,
  ],
};

function HttpLoaderFactory2(http: HttpBackend) {
  const version = environment.version;
  return new MultiTranslateHttpLoader(http, [
    {
      prefix: './assets/i18n/about-us/',
      suffix: `.json?v=${version}`,
      optional: true,
    },
    {
      prefix: './assets/i18n/account/',
      suffix: `.json?v=${version}`,
      optional: true,
    },
    {
      prefix: './assets/i18n/faqs/',
      suffix: `.json?v=${version}`,
      optional: true,
    },
    {
      prefix: './assets/i18n/guest/',
      suffix: `.json?v=${version}`,
      optional: true,
    },
    {
      prefix: './assets/i18n/how-it-works/',
      suffix: `.json?v=${version}`,
      optional: true,
    },
    {
      prefix: './assets/i18n/imprint/',
      suffix: `.json?v=${version}`,
      optional: true,
    },
    {
      prefix: './assets/i18n/contact/',
      suffix: `.json?v=${version}`,
      optional: true,
    },
    {
      prefix: './assets/i18n/property/',
      suffix: `.json?v=${version}`,
      optional: true,
    },
    {
      prefix: './assets/i18n/shared/',
      suffix: `.json?v=${version}`,
      optional: true,
    },
    {
      prefix: './assets/i18n/support/',
      suffix: `.json?v=${version}`,
      optional: true,
    },
    {
      prefix: './assets/i18n/reservations/',
      suffix: `.json?v=${version}`,
      optional: true,
    },
    {
      prefix: './assets/i18n/orders/',
      suffix: `.json?v=${version}`,
      optional: true,
    },
    {
      prefix: './assets/i18n/dashboard/',
      suffix: `.json?v=${version}`,
      optional: true,
    },
    {
      prefix: './assets/i18n/request/',
      suffix: `.json?v=${version}`,
      optional: true,
    },
    {
      prefix: './assets/i18n/list-data/',
      suffix: `.json?v=${version}`,
      optional: true,
    },
    {
      prefix: './assets/i18n/error/',
      suffix: `.json?v=${version}`,
      optional: true,
    },
    {
      prefix: './assets/i18n/discounts/',
      suffix: `.json?v=${version}`,
      optional: true,
    },
    {
      prefix: './assets/i18n/subscriptions/',
      suffix: `.json?v=${version}`,
      optional: true,
    },
    {
      prefix: './assets/i18n/outbox/',
      suffix: '.json',
      optional: true,
    },
    {
      prefix: './assets/i18n/statistics/',
      suffix: `.json?v=${version}`,
      optional: true,
    },
  ]);
}
